import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Zap, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const plan = searchParams.get('plan') || 'single';
  const sessionId = searchParams.get('session_id') || '';
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 600);
    return () => clearTimeout(t);
  }, []);

  // PostHog: payment_completed
  useEffect(() => {
    if (ready && sessionId) {
      try {
        window.posthog?.capture('payment_completed', {
          plan,
          session_id: sessionId,
          source: 'success_page',
        });
      } catch (_) { /* silent */ }
    }
  }, [ready, plan, sessionId]);

  const info = plan === 'pro'
    ? {
        title: 'BidSentry Pro Active',
        subtitle: 'Your £149/month subscription is now live.',
        next: 'You have unlimited bid checks, full analysis, and priority support.',
        cta: { label: 'Go to Dashboard', to: '/app' },
        secondary: { label: 'Run a Bid Check', to: '/check' },
      }
    : {
        title: 'Bid Check Unlocked',
        subtitle: 'Your £9 payment was successful.',
        next: 'Your paid bid check credit is ready to use.',
        cta: { label: 'Run a Bid Check', to: isAuthenticated && user?.has_org ? '/app/checks' : '/check' },
        secondary: isAuthenticated ? { label: 'My Checks', to: '/app/checks' } : { label: 'Create an Account', to: '/auth?mode=register' },
      };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="success-page">
      {/* Nav */}
      <header className="h-14 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-5xl mx-auto h-full px-4 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            <span className="font-black text-base tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center space-y-8">
          {!ready ? (
            <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto" data-testid="success-loading" />
          ) : (
            <>
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
              </div>

              <div>
                <h1
                  className="text-3xl sm:text-4xl font-black tracking-tight mb-2"
                  style={{ fontFamily: 'Barlow Condensed' }}
                  data-testid="success-title"
                >
                  {info.title}
                </h1>
                <p className="text-sm text-slate-400" data-testid="success-subtitle">{info.subtitle}</p>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-sm p-5">
                <p className="text-sm text-slate-300 leading-relaxed" data-testid="success-next-step">
                  {info.next}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Link to={info.cta.to}>
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold h-12 text-base" data-testid="success-primary-cta">
                    {info.cta.label}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to={info.secondary.to}>
                  <Button variant="outline" className="w-full border-slate-700 text-slate-400 hover:text-white h-10" data-testid="success-secondary-cta">
                    {info.secondary.label}
                  </Button>
                </Link>
              </div>

              <p className="text-[10px] text-slate-600">
                Payment confirmation has been sent to your email.<br />
                Access is granted automatically — no action needed.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
